import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../../core/data/redux/store";
import {
    selectActiveCompany,
    selectCompanyOptions,
    selectCompaniesLoading,
    selectFormsLoading,
    selectLeadForms,
    selectQueueFormId,
} from "../../../../../core/data/redux/leads-queue/selectors";

import {
    setActiveCompany,
    setQueueFormId,
} from "../../../../../core/data/redux/leads-queue/slice";
import { all_routes } from "../../../../router/all_routes";
import CommonSelect from "../../../../../core/common/commonSelect";
import CollapseHeader from "../../../../../core/common/collapse-header/collapse-header";

import type { Option } from "../../../../../core/common/commonSelect";

interface LeadsQueueHeaderProps {
    handleExport?: any
    isExportStarted?: Boolean
    activeView?: 'queue' | 'lead_bank'
}

const LeadsQueueHeader: React.FC<LeadsQueueHeaderProps> = ({ handleExport, isExportStarted, activeView }) => {
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();

    const activeCompany = useSelector(selectActiveCompany);
    const companyOptions = useSelector(selectCompanyOptions);

    const companiesLoading = useSelector(selectCompaniesLoading);
    const formsLoading = useSelector(selectFormsLoading);
    const isLoading = companiesLoading || formsLoading;

    const leadForms = useSelector(selectLeadForms);
    const formId = useSelector(selectQueueFormId);

    const handleCompanyChange = (selectedOption: any) => {
        if (!selectedOption) return;

        const [companyId, companyType] = selectedOption.value.split("-");
        const companyName = selectedOption.label.split("-")[0].trim();

        if (activeCompany?.company_id == companyId?.trim() && activeCompany?.company_type == companyType?.trim()) return;

        dispatch(
            setActiveCompany({
                company_id: companyId.trim(),
                company_name: companyName,
                company_type: companyType.trim(),
            }),
        );
    };

    const selectedFormOption: Option | undefined =
        formId && leadForms?.length
            ? {
                value: formId.toString(),
                label: leadForms?.find((f: any) => f.id == formId)?.name || "",
            }
            : undefined;

    return (
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
                <h2 className="mb-1">Leads In Queue</h2>
                <nav>
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item">
                            <Link to={all_routes.adminDashboard}>
                                <i className="ti ti-smart-home" />
                            </Link>
                        </li>
                        <li className="breadcrumb-item">CRM</li>
                        <li className="breadcrumb-item active" aria-current="page">
                            Queue
                        </li>
                    </ol>
                </nav>
            </div>

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                <div className="me-2 mb-2" title="Choose Company">
                    <CommonSelect
                        className="select"
                        options={companyOptions}
                        defaultValue={
                            activeCompany.company_id
                                ? {
                                    value: `${activeCompany.company_id}-${activeCompany.company_type}`,
                                    label: `${activeCompany.company_name} - ${activeCompany.company_type}`,
                                }
                                : undefined
                        }
                        onChange={handleCompanyChange}
                        isDisabled={isLoading}
                    />
                </div>

                {activeView === 'queue' && (
                    <>
                        <div className="me-2 mb-2" title="Choose Form" style={{ width: "220px" }}>
                            <CommonSelect
                                options={
                                    leadForms?.map((f: any) => ({
                                        value: f?.id?.toString(),
                                        label: f?.name,
                                    })) || []
                                }
                                defaultValue={selectedFormOption}
                                placeHolder={"Select Form"}
                                onChange={(opt: any) =>
                                    dispatch(setQueueFormId(opt?.value ? Number(opt.value) : null))
                                }
                                isDisabled={isLoading}
                            />
                        </div>

                    </>
                )}

                <div className="me-3 mb-2">
                    {activeView === 'queue' ? (
                        <Link
                            to={all_routes.leadBank + (location?.search || "")}
                            style={{ backgroundColor: "#1D71E3", border: "none", color: "#FFFFFF", padding: "10px 16px", borderRadius: "6px", fontWeight: 500 }}
                        >
                            <i className="ti ti-external-link me-1" />Go To Lead Bank
                        </Link>
                    ) : (
                        <Link
                            to={all_routes.leadsqueue + (location?.search || "")}
                            style={{ backgroundColor: "#1D71E3", border: "none", color: "#FFFFFF", padding: "10px 16px", borderRadius: "6px", fontWeight: 500 }}
                        >
                            <i className="ti ti-external-link me-1" />Go To Leads Queue
                        </Link>
                    )}
                </div>

                <div className="mb-2 me-2" title="Show Leads in Queue Logs">
                    <Link
                        to={`${all_routes.leadsInQueueHistory}${location?.search}`}
                        className="btn btn-info d-flex align-items-center"
                    >
                        <i className="ti ti-history me-1" />
                        Leads History
                    </Link>
                </div>
                {handleExport && (
                    <div className="mb-2 me-2" title="Export Data">
                        <button
                            className="btn btn-success d-flex align-items-center"
                            onClick={handleExport}
                            disabled={Boolean(isExportStarted)}
                        >
                            {isExportStarted ? (
                                <i className="ti ti-loader-2 me-1 animate-spin" />
                            ) : (
                                <i className="ti ti-database me-1" />
                            )}
                            {isExportStarted ? "Downloading" : "Export"}
                        </button>
                    </div>
                )}
                <div className="head-icons ms-2">
                    <CollapseHeader />
                </div>
            </div>
        </div >
    );
};

export default LeadsQueueHeader;
